import { useQuery } from 'react-query';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { specialCharactersRegex } from '../constants';
import { useTeamChatSelector } from '../providers/team-chat.provider/team-chat.provider';
import { getUserFullName } from '../utils';
import { useUserSearch } from './use-user-search';

/**
 * Finds a user based on mention text
 */
const findUserByMention = (mentionText: string, users: TeamChatTypes.User[]): TeamChatTypes.User | undefined => {
  if (mentionText.startsWith('@ORG_ID_')) {
    return users.find((user) => `@${user.userID}` === mentionText);
  }

  if (mentionText.startsWith('@')) {
    return users.find((user) => `@${getUserFullName(user.firstName, user.lastName)}`.trim() === mentionText);
  }

  return undefined;
};

export const useGetMentionedUser = ({ users, mentionText }: { users: TeamChatTypes.User[]; mentionText: string }) => {
  const { onSearchUsers } = useUserSearch();
  const setUsers = useTeamChatSelector((ctx) => ctx.cache.setUsers);
  const shouldFetchConversationUsers = useTeamChatSelector((ctx) => ctx.shouldFetchConversationUsers);

  const searchText = mentionText.replace('@', '').trim();

  return useQuery({
    queryKey: ['team-chat-mentioned-user', mentionText, users.length],
    queryFn: async () => {
      const user = findUserByMention(mentionText, users);
      if (!user && shouldFetchConversationUsers) {
        const res = await onSearchUsers(searchText);
        // Since onSettled will only get the mentioned user, so to get the searched user in cache we need to set it here
        setUsers(res);
        return res.reduce<TeamChatTypes.User | undefined>((found, u) => {
          const name = getUserFullName(u.firstName, u.lastName);
          if (name === searchText) {
            return u;
          }
          return found;
        }, undefined);
      }
      return user;
    },
    enabled: !!searchText.length && !searchText.match(specialCharactersRegex),
  });
};
